import { alpha, Box, BoxProps } from '@rossum/ui/material';
import {
  CONTROL_BAR_DISTANCE,
  GRID_LINE_WIDTH,
  INTERACTIVE_AREA_SIZE,
} from './constants';

export type SeparatorLineProps = BoxProps & {
  orientation: 'horizontal' | 'vertical';
  deleteButtonHovered: boolean;
  willBeDeletedByResize: boolean;
  isActive: boolean;
  isDragging: boolean;
  interactive: boolean;
  disabled: boolean;
};

export const SeparatorLine = ({
  orientation,
  deleteButtonHovered,
  isActive,
  isDragging,
  disabled,
  interactive,
  willBeDeletedByResize,
  ...props
}: SeparatorLineProps) => {
  const [primaryDim, secondaryDim, primaryPos, secondaryPos] =
    orientation === 'horizontal'
      ? (['width', 'height', 'top', 'left'] as const)
      : (['height', 'width', 'left', 'top'] as const);

  return (
    <Box
      {...props}
      sx={{
        position: 'absolute',

        borderColor: theme =>
          isActive ? alpha(theme.palette.background.paper, 0.1) : 'transparent',
        borderStyle: 'solid',
        borderWidth:
          orientation === 'horizontal'
            ? `${INTERACTIVE_AREA_SIZE}px 0`
            : `0 ${INTERACTIVE_AREA_SIZE}px`,
        backgroundClip: 'content-box',
        backgroundColor: theme =>
          disabled
            ? alpha(
                theme.palette.common.black,
                theme.palette.action.disabledOpacity
              )
            : deleteButtonHovered || willBeDeletedByResize
              ? alpha(theme.palette.warning.main, 0.5)
              : isActive
                ? theme.palette.primary.main
                : alpha(theme.palette.primary.main, 0.5),
        cursor: isDragging
          ? 'grabbing'
          : disabled || !interactive
            ? 'unset'
            : orientation === 'horizontal'
              ? 'row-resize'
              : 'col-resize',
        transition: theme =>
          theme.transitions.create(['background-color', 'border-color'], {
            duration: theme.transitions.duration.short,
          }),
        pointerEvents: disabled ? 'none' : 'auto',
        ...(disabled || !interactive
          ? {}
          : {
              [`&:hover`]: {
                backgroundColor: theme => theme.palette.primary.main,
                borderColor: theme =>
                  alpha(theme.palette.background.paper, 0.1),
              },
            }),
        ...props.sx,
      }}
      style={{
        [primaryPos]: -GRID_LINE_WIDTH - INTERACTIVE_AREA_SIZE,
        [secondaryPos]: deleteButtonHovered ? -CONTROL_BAR_DISTANCE : 0,
        [primaryDim]: `calc(100% + ${
          deleteButtonHovered ? CONTROL_BAR_DISTANCE : 0
        }px)`,
        [secondaryDim]: 2 * INTERACTIVE_AREA_SIZE + GRID_LINE_WIDTH,
        ...props.style,
      }}
    />
  );
};
